import { useState } from "react";
import { Plus, Star, X } from "lucide-react";
import { membershipApi } from "../api";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import type { MemberContact } from "../types";
import { MembershipAccess } from "./MembershipAccess";

const PAGE_SIZE = 5;

const inp =
  "w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";

interface ContactForm {
  contactName: string;
  designation: string;
  email: string;
  phoneNumber: string;
  primaryContact: boolean;
  receivesReminders: boolean;
  active: boolean;
}

const EMPTY: ContactForm = {
  contactName: "",
  designation: "",
  email: "",
  phoneNumber: "",
  primaryContact: false,
  receivesReminders: false,
  active: true,
};

export function MemberContactsPanel({
  memberId,
  contacts,
  onReload,
}: {
  memberId: number;
  contacts: MemberContact[];
  onReload: () => void;
}) {
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ContactForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const totalPages = Math.ceil(contacts.length / PAGE_SIZE) || 1;
  const visible = contacts.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const set = <K extends keyof ContactForm>(k: K, v: ContactForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.email && !form.phoneNumber) {
      setError("At least one of email or phone number is required.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await membershipApi.addContact(memberId, form);
      setForm(EMPTY);
      setShowForm(false);
      onReload();
    } catch {
      setError("Failed to save contact.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black text-slate-900">Contacts</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {contacts.length} contact{contacts.length !== 1 ? "s" : ""}
          </p>
        </div>
        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.EDIT, MEMBERSHIP_PERMISSIONS.MANAGE]}>
          <button
            type="button"
            onClick={() => { setShowForm((v) => !v); setError(""); }}
            className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-700 px-3 py-2 text-xs font-black text-white hover:bg-emerald-800"
          >
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {showForm ? "Cancel" : "Add Contact"}
          </button>
        </MembershipAccess>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="mb-3 text-xs font-black uppercase tracking-wide text-emerald-700">
            New Contact
          </p>
          {error && (
            <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-bold text-red-600">
              {error}
            </p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="label">Contact Name</label>
              <input value={form.contactName} onChange={(e) => set("contactName", e.target.value)} placeholder="Full name" className={inp} />
            </div>
            <div>
              <label className="label">Designation / Role</label>
              <input value={form.designation} onChange={(e) => set("designation", e.target.value)} placeholder="e.g. CEO, Finance Manager" className={inp} />
            </div>
            <div>
              <label className="label">Email *</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@company.com" className={inp} />
            </div>
            <div>
              <label className="label">Phone Number *</label>
              <input value={form.phoneNumber} onChange={(e) => set("phoneNumber", e.target.value)} placeholder="+254700000000" className={inp} />
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                <input type="checkbox" checked={form.primaryContact} onChange={(e) => set("primaryContact", e.target.checked)} className="accent-emerald-700" />
                <span className="text-sm font-bold text-slate-700">Primary contact</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                <input type="checkbox" checked={form.receivesReminders} onChange={(e) => set("receivesReminders", e.target.checked)} className="accent-emerald-700" />
                <span className="text-sm font-bold text-slate-700">Receives reminders</span>
              </label>
            </div>
          </div>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="mt-4 rounded-2xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Contact"}
          </button>
        </div>
      )}

      {/* Contact list */}
      {contacts.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">
          No contacts yet.
        </div>
      ) : (
        <>
          <div className="mt-4 grid gap-3">
            {visible.map((c) => (
              <div
                key={c.id}
                className={`rounded-2xl border p-4 ${
                  c.primaryContact ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {c.primaryContact && (
                        <Star className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500" />
                      )}
                      <span className="font-black text-slate-900">{c.contactName || "—"}</span>
                      {!c.active && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    {c.designation && (
                      <p className="mt-0.5 text-xs text-slate-500">{c.designation}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 text-xs text-slate-500">
                    {c.receivesReminders && (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 font-bold text-blue-600">
                        Gets reminders
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid gap-1.5 text-sm md:grid-cols-2">
                  {c.email && (
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <span className="text-xs font-black uppercase text-slate-400">Email</span>
                      <a href={`mailto:${c.email}`} className="font-bold hover:text-emerald-700">{c.email}</a>
                    </div>
                  )}
                  {c.phoneNumber && (
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <span className="text-xs font-black uppercase text-slate-400">Phone</span>
                      <a href={`tel:${c.phoneNumber}`} className="font-bold hover:text-emerald-700">{c.phoneNumber}</a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-slate-400">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, contacts.length)} of {contacts.length}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
